import React, { useEffect, useMemo, useState } from 'react'
import Select, { MultiValue } from 'react-select'
import CreatableSelect from 'react-select/creatable'

import InputGroup from '@components/InputGroup'
import MarkInput from '@components/MarkInput'
import {
	QuestionArticleInput,
	SuggestedArticle,
} from '@models/lawArticle.model'
import { disciplineApi } from '@services/DisciplineApi'

type Props = {
	title: string
	subMatterIds: number[]
	value?: QuestionArticleInput[]
	onChange: (value: QuestionArticleInput[]) => void
	followTitle?: boolean
}

type ArticleOption = { value: string; label: string }

const SUGGEST_DELAY_MS = 500

const withoutPrefix = (article: string) =>
	article.replace(/^\s*art(igo)?s?\.?\s*/i, '').trim()

const sameArticles = (a: QuestionArticleInput[], b: QuestionArticleInput[]) =>
	a.length === b.length &&
	a.every(
		(item, index) =>
			item.disciplineId === b[index].disciplineId &&
			withoutPrefix(item.article) === withoutPrefix(b[index].article)
	)

const styles = {
	wrapper: 'mt-4',
	empty: 'text-sm text-gray',
	group: 'mb-3',
	suggestion: 'mt-1 text-sm text-gray',
	useTitle: 'ml-1 text-blue underline',
	otherLaw: 'mt-2',
	otherLawCheck: 'mb-2',
}

const OTHER_LAW_HINT =
	'Conta só no raio-x desta lei: a questão não entra na lista de questões dela.'

export default function QuestionArticles({
	title,
	subMatterIds,
	value,
	onChange,
	followTitle = true,
}: Props) {
	const [suggest, { data: suggestion }] =
		disciplineApi.useSuggestQuestionArticlesMutation()
	const { data: disciplineList } = disciplineApi.useListQuery({
		page: 1,
		limit: 100000,
		search: '',
	})
	const [edited, setEdited] = useState(!followTitle)
	const [citesOtherLaw, setCitesOtherLaw] = useState(false)
	const [addedLaws, setAddedLaws] = useState<number[]>([])

	const subMatterKey = subMatterIds.join(',')

	useEffect(() => {
		if (!subMatterKey) {
			return undefined
		}

		const timer = setTimeout(() => {
			suggest({
				title: title ?? '',
				submatterIds: subMatterKey.split(',').map(Number),
			})
		}, SUGGEST_DELAY_MS)

		return () => clearTimeout(timer)
	}, [title, subMatterKey, suggest])

	const groups = useMemo(() => suggestion?.data ?? [], [suggestion])

	const fromTitle = useMemo<QuestionArticleInput[]>(
		() =>
			groups.flatMap((group) =>
				group.articles.map((article) => ({
					disciplineId: group.disciplineId,
					article: article.label,
				}))
			),
		[groups]
	)

	useEffect(() => {
		if (!edited && suggestion && !sameArticles(fromTitle, value ?? [])) {
			onChange(fromTitle)
		}
	}, [edited, suggestion, fromTitle, value, onChange])

	if (subMatterIds.length === 0) {
		return (
			<InputGroup label='Artigos' id='lawArticles' className={styles.wrapper}>
				<p className={styles.empty}>
					Escolha o sub-assunto para identificar os artigos da questão.
				</p>
			</InputGroup>
		)
	}

	const current = value ?? []
	const titleDiffers = edited && suggestion && !sameArticles(fromTitle, current)

	const ownGroups = groups.filter((group) => !group.otherLaw)
	const isOwn = (disciplineId: number) =>
		ownGroups.some((group) => group.disciplineId === disciplineId)
	const otherLawIds = suggestion
		? [
				...groups
					.filter((group) => group.otherLaw)
					.map((group) => group.disciplineId),
				...current.map((item) => item.disciplineId),
				...addedLaws,
		  ].filter(
				(disciplineId, index, ids) =>
					!isOwn(disciplineId) && ids.indexOf(disciplineId) === index
		  )
		: []
	const showOtherLaw = citesOtherLaw || otherLawIds.length > 0

	const lawName = (disciplineId: number) =>
		groups.find((group) => group.disciplineId === disciplineId)
			?.disciplineName ??
		disciplineList?.data.find((discipline) => discipline.id === disciplineId)
			?.name ??
		`Lei ${disciplineId}`

	const onGroupChange =
		(disciplineId: number) => (options: MultiValue<ArticleOption>) => {
			setEdited(true)
			onChange([
				...current.filter((item) => item.disciplineId !== disciplineId),
				...options.map((option) => ({
					disciplineId,
					article: withoutPrefix(option.value),
				})),
			])
		}

	const onCitesOtherLaw = (checked: boolean) => {
		setCitesOtherLaw(checked)
		if (!checked) {
			setAddedLaws([])
			setEdited(true)
			onChange(current.filter((item) => isOwn(item.disciplineId)))
		}
	}

	const articleField = (
		disciplineId: number,
		label: string,
		suggested: SuggestedArticle[],
		hint: string
	) => (
		<InputGroup
			key={disciplineId}
			label={label}
			id={`lawArticles-${disciplineId}`}
			className={styles.group}
			hint={hint}
		>
			<CreatableSelect<ArticleOption, true>
				inputId={`lawArticles-${disciplineId}`}
				isMulti
				value={current
					.filter((item) => item.disciplineId === disciplineId)
					.map((item) => ({
						value: item.article,
						label: withoutPrefix(item.article),
					}))}
				options={suggested.map((article) => ({
					value: article.label,
					label: article.label,
				}))}
				onChange={onGroupChange(disciplineId)}
				isValidNewOption={(input) => /\d/.test(input)}
				formatCreateLabel={(input) =>
					`Acrescentar Art. ${withoutPrefix(input)}`
				}
				noOptionsMessage={() => 'Digite o número do artigo'}
				placeholder='Nenhum artigo'
				classNamePrefix='react-select'
			/>
		</InputGroup>
	)

	return (
		<div className={styles.wrapper}>
			{ownGroups.map((group) =>
				articleField(
					group.disciplineId,
					ownGroups.length > 1
						? `Artigos - ${group.disciplineName}`
						: 'Artigos',
					group.articles,
					'Lidos da tag do título. Digite um número para acrescentar (ex.: 1.015-A).'
				)
			)}
			{suggestion && (
				<div className={styles.otherLaw}>
					<MarkInput
						type='checkbox'
						className={styles.otherLawCheck}
						multiCheckbox
						itens={[
							{
								id: 'citesOtherLaw',
								title: 'Essa questão cita outro diploma legal?',
								checked: showOtherLaw,
							},
						]}
						selectedId={showOtherLaw ? 'citesOtherLaw' : ''}
						onChange={(event) => onCitesOtherLaw(event.currentTarget.checked)}
					/>
					{showOtherLaw &&
						otherLawIds.map((disciplineId) =>
							articleField(
								disciplineId,
								`Artigos - ${lawName(disciplineId)}`,
								groups.find((group) => group.disciplineId === disciplineId)
									?.articles ?? [],
								OTHER_LAW_HINT
							)
						)}
					{showOtherLaw && (
						<InputGroup
							label='Acrescentar lei'
							id='otherLawDiscipline'
							className={styles.group}
						>
							<Select<{ value: number; label: string }>
								inputId='otherLawDiscipline'
								value={null}
								options={(disciplineList?.data ?? [])
									.filter(
										(discipline) =>
											!isOwn(discipline.id) &&
											!otherLawIds.includes(discipline.id)
									)
									.map((discipline) => ({
										value: discipline.id,
										label: discipline.name,
									}))}
								onChange={(option) =>
									option && setAddedLaws([...addedLaws, option.value])
								}
								placeholder='Escolha a lei do outro artigo'
								classNamePrefix='react-select'
							/>
						</InputGroup>
					)}
				</div>
			)}
			{titleDiffers && (
				<p className={styles.suggestion}>
					O título indica{' '}
					{fromTitle.length
						? fromTitle.map((item) => `Art. ${item.article}`).join(', ')
						: 'nenhum artigo'}
					.
					<button
						type='button'
						className={styles.useTitle}
						onClick={() => {
							setEdited(false)
							setAddedLaws([])
							onChange(fromTitle)
						}}
					>
						Usar artigos do título
					</button>
				</p>
			)}
		</div>
	)
}
